import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { User, UserRole } from "@/types/user";

const ADMIN_TOKEN_KEY = "admin_token";
const ADMIN_USER_KEY = "admin_user";

function decodeToken(
  token: string
): { exp?: number; role?: string; name?: string; email?: string } | null {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
}

function isTokenValid(token: string): boolean {
  const payload = decodeToken(token);
  if (!payload?.exp) return false;
  return payload.exp > Math.floor(Date.now() / 1000);
}

export function useAdminAuth() {
  const navigate = useNavigate();

  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  const isAuthenticated = !!token && isTokenValid(token);

  const userStr = localStorage.getItem(ADMIN_USER_KEY);
  const user: User | null = userStr ? JSON.parse(userStr) : null;

  const isSuperAdmin = user?.role === UserRole.SUPERADMIN;
  const isCorporateAdmin = user?.role === UserRole.CORPORATE_ADMIN;
  const isMaatraAdmin =
    user?.role === UserRole.SUPERADMIN || user?.role === UserRole.ADMIN;
  const organisationId = user?.organisationId ?? null;

  const setSession = useCallback((token: string, user: User) => {
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
    localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    navigate("/admin/login");
  }, [navigate]);

  return {
    isAuthenticated,
    user,
    isSuperAdmin,
    isCorporateAdmin,
    isMaatraAdmin,
    organisationId,
    token,
    setSession,
    logout,
  };
}
